/**
 * MISSION 9 - PHASE 2: USER DETAIL MODAL
 * Comprehensive user view with all data, analytics, and management actions
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  X,
  User,
  Mail,
  Calendar,
  Activity,
  Target,
  TrendingUp,
  DollarSign,
  Smartphone,
  Shield,
  AlertTriangle,
  Key,
  Copy,
  Check,
  Trash2,
  Settings
} from 'lucide-react';
import { storage } from '../../services/storage';
import { SevenFreeDaysTestTools } from './SevenFreeDaysTestTools';
import { GiveFreeMonthPanel } from './GiveFreeMonthPanel';
import { UserPartnerSection } from './UserPartnerSection';
import type { UserPartnerInfo } from '../../pages/admin/partners/partnerContactsLogic';

import { API_BASE_URL } from '../../utils/api'; // Centralized environment-aware API URL

interface UserDetail {
  // Basic Info
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  
  // Status & Lifecycle
  status: string;
  authMethod: string;
  createdAt: string;
  firstLoginAt?: string;
  lastActiveAt?: string;
  
  // Onboarding
  onboardingStep: number;
  onboardingCompleted: boolean;
  
  // Goals & Activity
  goalCount: number;
  activePlanCount: number;
  workoutCount: number;
  lastWorkoutAt?: string;
  
  // LLM Usage
  llmCallCount: number;
  totalLLMCost: number;
  avgCostPerCall: number;
  llmProfile?: {
    profile_name: string;
    display_name: string;
    assigned_at: string;
  };
  
  // Device & Security
  deviceBound: boolean;
  deviceBoundAt?: string;
  deviceFingerprint?: string;
  
  // Legal
  eulaAccepted: boolean;
  eulaAcceptedAt?: string;
  
  // Admin
  adminNotes: string;
  source: string;

  // Partner (partner launch): undefined = the server does not send it; null = no partner source
  partner?: UserPartnerInfo | null;
}

interface LLMProfile {
  profile_name: string;
  display_name: string;
  description: string;
  is_default: boolean;
  is_system: boolean;
  user_count: number;
  created_by: string;
  created_at: string;
}

interface UserDetailModalProps {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated?: () => void;
  onDeleteUserData?: (userId: string, userEmail: string, userName: string) => void;
}

export const UserDetailModal: React.FC<UserDetailModalProps> = ({
  userId,
  isOpen,
  onClose,
  onUserUpdated,
  onDeleteUserData
}) => {
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [resettingDevice, setResettingDevice] = useState(false);
  
  // LLM Profile Management
  const [availableProfiles, setAvailableProfiles] = useState<LLMProfile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<string>('');
  const [assigningProfile, setAssigningProfile] = useState(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && userId) {
      // Reset state when modal opens
      setUser(null);
      setError(null);
      setLoading(true);
      fetchUserDetail();
      fetchAvailableProfiles();
    }
  }, [isOpen, userId]);
  
  useEffect(() => {
    if (user?.llmProfile) {
      setSelectedProfile(user.llmProfile.profile_name);
    } else {
      // Default profile if no profile assigned
      const defaultProfile = availableProfiles.find(p => p.is_default);
      if (defaultProfile) {
        setSelectedProfile(defaultProfile.profile_name);
      }
    }
  }, [user, availableProfiles]);

  const fetchUserDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      
      // Use /detail (singular) to match admin_user_management router
      const response = await fetch(`${API_BASE}/users/${userId}/detail`, {
        headers: {
          'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch user details' }));
        throw new Error(errorData.detail || `HTTP ${response.status}: Failed to fetch user details`);
      }
      
      const data = await response.json();
      
      // admin_user_management returns flat structure that matches UserDetail interface
      const userData: UserDetail = {
        id: data.id,
        email: data.email,
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        name: data.name || data.email,
        status: data.status,
        authMethod: data.authMethod,
        createdAt: data.createdAt,
        firstLoginAt: data.firstLoginAt,
        lastActiveAt: data.lastActiveAt,
        onboardingStep: data.onboardingStep || 0,
        onboardingCompleted: data.onboardingCompleted || false,
        goalCount: data.goalCount || 0,
        activePlanCount: data.activePlanCount || 0,
        workoutCount: data.workoutCount || 0,
        lastWorkoutAt: data.lastWorkoutAt,
        llmCallCount: data.llmCallCount || 0,
        totalLLMCost: data.totalLLMCost || 0,
        avgCostPerCall: data.avgCostPerCall || 0,
        deviceBound: data.deviceBound || false,
        deviceBoundAt: data.deviceBoundAt,
        deviceFingerprint: data.deviceFingerprint,
        eulaAccepted: data.eulaAccepted || false,
        eulaAcceptedAt: data.eulaAcceptedAt,
        adminNotes: data.adminNotes || '',
        source: data.source || 'unknown',
        // The backend has always sent llmProfile — this mapping silently
        // dropped it, so the modal showed 'default' for every user
        // regardless of their real assignment (found 2026-08-01).
        llmProfile: data.llmProfile || undefined,
        // Kept as sent: absent (an older server) hides the Partner section; null means "none".
        partner: data.partner === undefined ? undefined : (data.partner ?? null)
      };
      
      setUser(userData);
    } catch (error: any) {
      console.error('Failed to fetch user details:', error);
      setError(error.message || 'Failed to load user details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateLoginToken = async () => {
    setGeneratingToken(true);
    try {
      // Absolute API base + auth, same reason as the notes save below: a
      // relative URL resolves against app.coachcedar.com, whose ALB 301s to
      // itself (:443) forever — ERR_TOO_MANY_REDIRECTS. Endpoint also
      // requires get_current_admin, so the header is not optional.
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      const response = await fetch(`${API_BASE}/users/${userId}/generate-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      if (!response.ok) {
        const detail = await response.text().catch(() => '');
        throw new Error(`${response.status} ${response.statusText}${detail ? ` — ${detail.slice(0, 200)}` : ''}`);
      }
      const data = await response.json();
      
      // Copy token to clipboard
      await navigator.clipboard.writeText(data.token);
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 3000);
      
      // Refresh user data
      await fetchUserDetail();
      if (onUserUpdated) onUserUpdated();
    } catch (error) {
      console.error('Failed to generate login token:', error);
      alert('Failed to generate login token');
    } finally {
      setGeneratingToken(false);
    }
  };

  const resetDeviceBinding = async () => {
    if (!confirm('Reset device binding for this user? They will need to re-bind on next login.')) {
      return;
    }
    
    setResettingDevice(true);
    try {
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      const response = await fetch(`${API_BASE}/users/${userId}/reset-device`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      if (!response.ok) {
        const detail = await response.text().catch(() => '');
        throw new Error(`${response.status} ${response.statusText}${detail ? ` — ${detail.slice(0, 200)}` : ''}`);
      }

      // Refresh user data
      await fetchUserDetail();
      if (onUserUpdated) onUserUpdated();
      alert('Device binding reset successfully');
    } catch (error) {
      console.error('Failed to reset device binding:', error);
      alert(`Failed to reset device binding: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setResettingDevice(false);
    }
  };

  // LLM Profile Management
  const fetchAvailableProfiles = async () => {
    try {
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      
      const response = await fetch(`${API_BASE}/llm-profiles/profiles`, {
        headers: {
          'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch LLM profiles');
      }
      
      const data = await response.json();
      setAvailableProfiles(data.profiles || []);
    } catch (error) {
      console.error('Failed to fetch LLM profiles:', error);
    }
  };

  const handleProfileAssignment = async (profileName: string) => {
    if (!userId) return;
    
    setAssigningProfile(true);
    try {
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      
      const response = await fetch(`${API_BASE}/llm-profiles/users/assign-profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          user_id: userId,
          profile_name: profileName,
          notes: `Profile changed to ${profileName} by admin`
        })
      });
      
      if (!response.ok) {
        const detail = await response.text().catch(() => '');
        throw new Error(`${response.status} ${response.statusText}${detail ? ` — ${detail.slice(0, 200)}` : ''}`);
      }

      // Update local state
      setSelectedProfile(profileName);

      // Refresh user data to show updated profile
      await fetchUserDetail();
      if (onUserUpdated) onUserUpdated();

      alert(`LLM profile changed to ${profileName} successfully`);
    } catch (error) {
      console.error('Failed to assign LLM profile:', error);
      alert(`Failed to change LLM profile: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setAssigningProfile(false);
    }
  };

  // Debug logging
  useEffect(() => {
    console.log('UserDetailModal render check:', { isOpen, userId, loading, error: error || null, hasUser: !!user });
  }, [isOpen, userId, loading, error, user]);

  if (!isOpen) {
    console.log('UserDetailModal: Not rendering because isOpen is false');
    return null;
  }

  console.log('UserDetailModal: Rendering modal');

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] flex flex-col">
        {/* Header — fixed */}
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0 rounded-t-lg">
          <div className="flex items-center space-x-3">
            <User className="h-6 w-6 text-green-600" />
            <h2 className="text-xl font-bold text-gray-900">User Details</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-green-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading user details...</p>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <p className="text-red-600 font-semibold mb-2">Failed to load user details</p>
            <p className="text-gray-600 text-sm mb-4">{error}</p>
            <button
              onClick={fetchUserDetail}
              className="px-4 py-2 text-white rounded-lg" style={{ backgroundColor: 'var(--cedar-primary)' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--cedar-dark)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--cedar-primary)'; }}
            >
              Retry
            </button>
          </div>
        ) : user ? (
          <div className="p-4 space-y-3">
            {/* Basic Info */}
            <div className="bg-gray-50 rounded-md p-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Basic Information</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-500">Name</label>
                  <p className="text-sm font-medium text-gray-900">{user.name}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Email</label>
                  <p className="text-sm font-medium text-gray-900">{user.email}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Status</label>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                    user.status === 'active' ? 'bg-green-100 text-green-800' :
                    user.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {user.status}
                  </span>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Auth</label>
                  <p className="text-sm font-medium text-gray-900">{user.authMethod}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Created</label>
                  <p className="text-sm text-gray-900">{new Date(user.createdAt).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Last Active</label>
                  <p className="text-sm text-gray-900">{user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleString() : 'Never'}</p>
                </div>
              </div>
            </div>

            {/* Partner: which partner this account came through, and the linked partner contact */}
            <UserPartnerSection partner={user.partner} />

            {/* Activity + LLM in one row */}
            <div className="grid grid-cols-2 gap-3">
              {/* Activity Stats */}
              <div className="bg-blue-50 rounded-md p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Activity</h3>
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900">{user.goalCount}</p>
                    <p className="text-xs text-gray-500">Goals</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900">{user.activePlanCount}</p>
                    <p className="text-xs text-gray-500">Plans</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900">{user.workoutCount}</p>
                    <p className="text-xs text-gray-500">Workouts</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900">{user.onboardingStep}/7</p>
                    <p className="text-xs text-gray-500">Onboard</p>
                  </div>
                </div>
              </div>

              {/* LLM Usage */}
              <div className="bg-green-50 rounded-md p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">LLM Costs</h3>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900">{user.llmCallCount.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Calls</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold" style={{ color: 'var(--cedar-primary)' }}>${user.totalLLMCost.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Cost</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900">${user.avgCostPerCall.toFixed(4)}</p>
                    <p className="text-xs text-gray-500">Avg</p>
                  </div>
                </div>
              </div>
            </div>

            {/* LLM Profile + Device in one row */}
            <div className="grid grid-cols-2 gap-3">
              {/* LLM Profile */}
              <div className="bg-indigo-50 rounded-md p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">LLM Profile</h3>
                <div className="space-y-2">
                  {user.llmProfile && (
                    <p className="text-xs text-indigo-600 font-medium">{user.llmProfile.display_name}</p>
                  )}
                  <div className="flex items-center space-x-2">
                    <select
                      value={selectedProfile}
                      onChange={(e) => setSelectedProfile(e.target.value)}
                      disabled={assigningProfile}
                      className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-md"
                    >
                      {availableProfiles.length === 0 && (
                        <option value="">Loading...</option>
                      )}
                      {availableProfiles.map(profile => (
                        <option key={profile.profile_name} value={profile.profile_name}>
                          {profile.display_name}{profile.is_default ? ' (Default)' : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleProfileAssignment(selectedProfile)}
                      disabled={assigningProfile || selectedProfile === user?.llmProfile?.profile_name}
                      className="px-2 py-1 text-xs text-white rounded-md disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap" style={{ backgroundColor: 'var(--cedar-primary)' }}
                    >
                      {assigningProfile ? '...' : 'Assign'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Device & Security */}
              <div className="bg-purple-50 rounded-md p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Device & Security</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Device</span>
                    <div className="flex items-center space-x-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        user.deviceBound ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {user.deviceBound ? 'Bound' : 'Unbound'}
                      </span>
                      {user.deviceBound && (
                        <button onClick={resetDeviceBinding} disabled={resettingDevice} className="px-1.5 py-0.5 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 disabled:opacity-50">
                          Reset
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">EULA</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      user.eulaAccepted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.eulaAccepted ? 'Accepted' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Actions — compact row */}
            <div className="bg-gray-50 rounded-md p-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Admin Actions</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={generateLoginToken}
                  disabled={generatingToken}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-1"
                >
                  {generatingToken ? (
                    <span>Generating...</span>
                  ) : copiedToken ? (
                    <>
                      <Check className="h-3 w-3" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Key className="h-3 w-3" />
                      <span>Login Token</span>
                    </>
                  )}
                </button>
                {onDeleteUserData && user && (
                  <button
                    onClick={() => {
                      if (confirm(`Are you sure you want to delete all data for ${user.name} (${user.email})? This action cannot be undone.`)) {
                        onDeleteUserData(user.id, user.email, user.name);
                      }
                    }}
                    className="px-3 py-2 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 flex items-center space-x-1"
                  >
                    <Trash2 className="h-3 w-3" />
                    <span>Delete</span>
                  </button>
                )}
              </div>
            </div>

            {/* Seven Free Days test tools — the 7-day clock switches and fast-forward (B13) */}
            <SevenFreeDaysTestTools userId={user.id} />

            {/* Give a free month (free month PR-2) — hidden when FF_ADMIN_FREE_MONTH is off or the
                session cannot write */}
            <GiveFreeMonthPanel userId={user.id} />

            {/* Admin Notes — compact */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Admin Notes</label>
              <textarea
                value={user.adminNotes || ''}
                onChange={(e) => setUser({ ...user, adminNotes: e.target.value })}
                rows={2}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 focus:border-green-500"
                placeholder="Add admin notes..."
              />
            </div>
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>Failed to load user details</p>
          </div>
        )}

        </div>
        {/* Footer — fixed */}
        <div className="bg-gray-50 border-t px-6 py-4 flex justify-end space-x-3 flex-shrink-0 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
          {user && (
            <button
              onClick={async () => {
                // Apply a pending profile selection first. The LLM-profile
                // dropdown only stages local state — the API call lives behind
                // its own small "Assign" button, and a dropdown change followed
                // by "Save Changes" produced a notes-success alert that read as
                // the model change having applied when it hadn't (2026-08-01,
                // Gabriel assignment silently lost).
                if (selectedProfile && selectedProfile !== user?.llmProfile?.profile_name) {
                  await handleProfileAssignment(selectedProfile);
                }
                // Save admin notes. Must use the absolute API base — a relative
                // URL resolves against app.coachcedar.com (CloudFront frontend)
                // in production and redirect-loops (ERR_TOO_MANY_REDIRECTS,
                // found 2026-07-31). Auth header was also missing.
                try {
                  const API_BASE = window.location.hostname === 'localhost' ?
                    '/api/admin' : `${API_BASE_URL}/api/admin`;
                  const response = await fetch(`${API_BASE}/users/${userId}/notes`, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
                      'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({ admin_notes: user.adminNotes })
                  });
                  if (!response.ok) {
                    const detail = await response.text().catch(() => '');
                    throw new Error(`${response.status} ${response.statusText}${detail ? ` — ${detail.slice(0, 200)}` : ''}`);
                  }
                  if (onUserUpdated) onUserUpdated();
                  alert('Admin notes saved successfully');
                } catch (error) {
                  alert(`Failed to save admin notes: ${error instanceof Error ? error.message : String(error)}`);
                }
              }}
              className="px-4 py-2 text-white rounded-lg" style={{ backgroundColor: 'var(--cedar-primary)' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--cedar-dark)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--cedar-primary)'; }}
            >
              Save Changes
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UserDetailModal;

